/**
 * Money field.
 *
 * Holds a STRING throughout. Sanitises as the user types (digits and one
 * decimal point, max 2dp), normalises on blur. Never touches Number().
 */
import { forwardRef, useImperativeHandle, useRef, type KeyboardEvent } from 'react'
import { normalise, sanitize } from './money'
import { c, tokens } from '../theme'

export interface AmountHandle { focus: () => void; select: () => void }

interface Props {
  value: string
  onChange: (v: string) => void
  onCommit?: () => void
  disabled?: boolean
  placeholder?: string
  dimmed?: boolean
}

const AmountInput = forwardRef<AmountHandle, Props>(function AmountInput(
  { value, onChange, onCommit, disabled, placeholder, dimmed }, ref,
) {
  const el = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => ({
    focus: () => el.current?.focus(),
    select: () => el.current?.select(),
  }), [])

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); onChange(normalise(value)); onCommit?.() }
  }

  return (
    <input
      ref={el}
      className="num"
      inputMode="decimal"
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(sanitize(e.target.value))}
      onBlur={() => onChange(normalise(value))}
      onFocus={() => el.current?.select()}
      onKeyDown={onKeyDown}
      style={{
        width: '100%',
        height: tokens.rowHeight - 8,
        padding: '0 8px',
        border: '1px solid transparent',
        borderRadius: 4,
        background: 'transparent',
        font: 'inherit',
        // A+ sets amounts in the UI face with tabular figures, not mono.
        fontFamily: tokens.sans,
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 500,
        textAlign: 'right',
        color: dimmed ? c.inkMuted : c.ink,
        outline: 'none',
      }}
    />
  )
})

export default AmountInput
